import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton, withStyles } from 'vopay';
import { BodyText, Title, UtilButton } from 'UI';
import useFetchPartnerAccountDetails from '@/components/Entities/hooks/useApi/useFetchPartnerAccountDetails';
import useFetchOnboardingAppUrl from '@/components/Entities/hooks/useApi/useFetchOnboardingAppUrl';
import usePartnerBearer from '@/components/Entities/hooks/useApi/usePartnerBearer';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePendingData } from '@/providers/PendingData.provider';
import { useToast } from '@/providers/Toast.provider';
import { useDialog, type OpenDialogProps } from '@/providers/Dialog.provider';
import AccountCard from '@/components/Entities/Accounts/AccountHeaderCards/AccountCard';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';
import AvailableBalanceCard from '@/components/Entities/Accounts/AccountHeaderCards/AvailableBalanceCard';
import TransactionLimitsCard from '@/components/Entities/Accounts/AccountHeaderCards/TransactionLimitsCard';
import { getAccountCredentials } from '@/components/Entities/Accounts/AccountHeaderCards/accountCredentials';
import { getOutboundLimits } from '@/components/Entities/Accounts/AccountHeaderCards/accountLimits';
import AccountContext from '../AccountContext';
import AccountTabs from './AccountTabs';
import toAccountBalance from '@/components/Entities/Accounts/toAccountBalance';
import SendEmailDialog, { type SendEmailDialogResult } from '@/components/Accounts/Account/SendEmailDIalog';
import styles from './AccountDetailsPage.module.scss';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

const cn = withStyles(styles);

/**
 * The full account page (spec 5.6): the header cards the details drawer also renders, then the tabs.
 * The account is fetched here with `IncludeAccountDetails`, which is what carries the credentials,
 * reserve policy, limit usage and the Details tab's profile; the embedded tabs still read the list
 * fetch that `AccountsContainer` runs for the same account.
 */
export default function AccountDetailsPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { accountId } = useParams<{ accountId: string }>();
  const history = useHistory();
  const { details, balances, hasAccountsSettled } = useContext(AccountContext);
  const { hasPendingData } = usePendingData();
  const { canAccessOnboardingTokenPanel, canSeeAccountCredential } = useModuleFeatureFlag('accounts');
  const toast = useToast();
  const dialog = useDialog();

  // The list hands over the currency of the row that was clicked; an account can hold several. It is
  // remembered here because an embedded list writing its filters to the query string drops it.
  const currencyParam = new URLSearchParams(useLocation().search).get('currency');
  const [currency, setCurrency] = useState(currencyParam);

  useEffect(() => {
    if (currencyParam) setCurrency(currencyParam);
  }, [currencyParam]);

  const { account, fetchPartnerAccountDetails, reset } = useFetchPartnerAccountDetails();

  const {
    onboardingUrl,
    isOnboardingEmailAllowed,
    fetchOnboardingAppUrl,
    reset: resetOnboardingUrl,
  } = useFetchOnboardingAppUrl();

  const [hasDetailsFailed, setHasDetailsFailed] = useState(false);

  const loadAccountDetails = () =>
    fetchPartnerAccountDetails(accountId).catch((error) => {
      // The hook has logged the cause. The tabs the list fetch feeds still render; the header and
      // the Details tab show as unavailable rather than loading forever.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasDetailsFailed(true);

      toast.neutral(t('loadError', { defaultValue: 'Could not load the account: {{errorMessage}}', errorMessage }));
    });

  useEffect(() => {
    reset();
    // Without this the previous account's link would still be here — copyable and emailable — until
    // this account's own request lands.
    resetOnboardingUrl();
    setHasDetailsFailed(false);

    loadAccountDetails();
  }, [accountId]);

  const accountBalance = useMemo(() => account && toAccountBalance(account, currency), [account, currency]);

  // Same gate as the drawer: an Active account, and a partner allowed to see credentials.
  const credentials =
    accountBalance && accountBalance.IsActive && canSeeAccountCredential
      ? getAccountCredentials(accountBalance.Token)
      : null;

  // The list fetch already carries the status, so a failed details request still shows an onboarding
  // account as pending instead of as an ordinary account with empty cards.
  const listedAccount = useMemo(() => balances.find((item) => item.AccountID === accountId), [balances, accountId]);
  const status = account?.AccountStatus ?? listedAccount?.AccountStatus;
  const isActive = account?.IsActive ?? listedAccount?.IsActive;

  const isPending = canAccessOnboardingTokenPanel && !isActive && status === 'Application Pending';

  // The owning partner, undefined until the details or the list row arrive; the backend always sends it on
  // both. An empty ID (a partner without an APIAccountID) means the session's own token, which the backend
  // refuses for a child partner's account. Once the details have failed and no list row holds the account,
  // the session's token is the only one left to try.
  const ownerPartnerAccountID =
    account?.PartnerAccountID ?? listedAccount?.PartnerAccountID ?? (hasDetailsFailed ? '' : undefined);

  const partnerBearer = usePartnerBearer(ownerPartnerAccountID ?? '');

  useEffect(() => {
    // The bearer resolves asynchronously (empty on the first render), so this waits for it and
    // re-runs once it lands; without that the request goes out unsigned and the link never loads.
    if (!isPending || ownerPartnerAccountID === undefined || !partnerBearer) return;

    fetchOnboardingAppUrl(accountId, partnerBearer).catch((error) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('OnboardingUrl.loadError', {
          defaultValue: 'Could not load the onboarding link: {{errorMessage}}',
          errorMessage,
        }),
      );
    });
  }, [isPending, ownerPartnerAccountID, partnerBearer, accountId]);

  // The list writes its filters with history.replace, so the entry behind this page is the list as the
  // user had it. Going back restores it; a page opened cold (a refresh or a shared link) has nothing
  // behind it, so it falls back to the bare list.
  const arrivedFromApp = useRef(history.action === 'PUSH');

  const handleBackClick = () => {
    if (arrivedFromApp.current) {
      history.goBack();

      return;
    }

    history.push(ACCOUNTS_PATH);
  };

  const handleCopyClick = () => {
    if (!onboardingUrl) return;

    navigator?.clipboard?.writeText(onboardingUrl);
    toast.success(tGlobal('copied'));
  };

  const handleSendEmail = async () => {
    if (!account) return;

    const BoundDialog = ({ onConfirm, onCancel }: OpenDialogProps<SendEmailDialogResult>) => (
      <SendEmailDialog
        onConfirm={onConfirm}
        onCancel={onCancel}
        email={account.Email}
        // This page also serves sub-accounts, and /resend looks the entity up by type and ID together.
        // ParentAccounts is the account's parent chain, empty only for a top-level account.
        entityType={account.ParentAccounts.length > 0 ? 'SubAccount' : 'Account'}
        entityID={accountId}
      />
    );

    // Success needs no follow-up here; a failure is toasted by the dialog itself.
    await dialog.open(BoundDialog);
  };

  return (
    <div className={cn('page')}>
      <UtilButton size="medium" iconLeft="chevron_left" onClick={handleBackClick}>
        {tGlobal('Buttons.back')}
      </UtilButton>

      {/* Figma: H1/32 (the frame sets it in Semi Bold; Title's H1 is Bold) */}
      <Title>{t('title', { defaultValue: 'Account Details' })}</Title>
      {!accountBalance && !hasDetailsFailed && <Skeleton height={148} data-testid="accountHeaderCardsLoading" />}

      {hasDetailsFailed && (
        <BodyText data-testid="accountHeaderCardsUnavailable">
          {t('headerUnavailable', { defaultValue: 'Account details are unavailable.' })}
        </BodyText>
      )}

      {accountBalance && account && (
        <div className={cn('headerCards')} data-testid="accountHeaderCards">
          {/* A sub-account's parents are its trail back up; each opens on this same page. */}
          <AccountCard
            account={accountBalance}
            credentials={credentials}
            onParentAccountClick={(parent) => history.push(accountPageRoute.getPath({ accountID: parent.AccountID }))}
          />

          <AvailableBalanceCard
            currency={accountBalance.Currency}
            availableBalance={accountBalance.AvailableBalance}
            pendingBalance={accountBalance.PendingBalance}
            reserve={accountBalance.Reserve}
            rollingReserves={account.RollingReserves ?? []}
            compact
          />

          <TransactionLimitsCard
            currency={accountBalance.Currency}
            limits={getOutboundLimits(account)}
            usage={account.LimitUsage?.Outbound ?? null}
          />
        </div>
      )}

      {/* The tabs read the list fetch AccountsContainer runs for this account — a different request
          from this page's own, so the wait ends on that one: once it has answered without this
          account, it failed (it reports its own toast) or the account is not the partner's. */}
      {!details[accountId] && !hasAccountsSettled && <Skeleton height={320} data-testid="accountTabsLoading" />}

      {!details[accountId] && hasAccountsSettled && (
        <BodyText data-testid="accountTabsUnavailable">
          {t('tabsUnavailable', { defaultValue: 'This account could not be loaded.' })}
        </BodyText>
      )}

      {details[accountId] && (
        <AccountTabs
          account={hasDetailsFailed ? null : account}
          isPending={isPending}
          status={status}
          onContactsUpdated={loadAccountDetails}
          onBoardingLink={onboardingUrl ?? null}
          hasValidOnboardingUrl={Boolean(onboardingUrl)}
          isOnboardingEmailAllowed={isOnboardingEmailAllowed}
          onCopy={handleCopyClick}
          onSendEmail={handleSendEmail}
        />
      )}

      <Prompt when={hasPendingData} message={t('unsavedChangesPrompt')} />
    </div>
  );
}
